import { useEffect, useState } from 'react'
import { loadMetrics, loadPeriods, type Period } from './data'
import { duration, summarize, type Metric } from './metrics'

const statusLabel = { PREVIEW: 'Prévia', FINAL: 'Definitivo', CLOSED: 'Fechado' }
const number = new Intl.NumberFormat('pt-BR')
function monthLabel(value: string) {
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${value}T12:00:00Z`))
}

export function Dashboard() {
  const [periods, setPeriods] = useState<Period[]>([])
  const [periodId, setPeriodId] = useState('')
  const [queueId, setQueueId] = useState('')
  const [metrics, setMetrics] = useState<Metric[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    loadPeriods(controller.signal).then(result => {
      if (controller.signal.aborted) return
      setPeriods(result)
      setPeriodId(previous => result.some(p => p.id === previous) ? previous : result[0]?.id ?? '')
      if (!result.length) setLoading(false)
    }).catch(() => {
      if (!controller.signal.aborted) { setError(true); setLoading(false) }
    })
    return () => controller.abort()
  }, [attempt])

  useEffect(() => {
    if (!periodId) return
    const controller = new AbortController()
    loadMetrics(periodId, controller.signal).then(result => {
      if (!controller.signal.aborted) { setMetrics(result); setLoading(false) }
    }).catch(() => {
      if (!controller.signal.aborted) { setError(true); setLoading(false) }
    })
    return () => controller.abort()
  }, [periodId, attempt])

  const filtered = metrics.filter(metric => !queueId || metric.queues.id === queueId)
  const totals = summarize(filtered)
  const queues = [...new Map(metrics.map(metric => [metric.queues.id, metric.queues])).values()]
    .sort((a, b) => a.display_name.localeCompare(b.display_name, 'pt-BR'))
  const selected = periods.find(period => period.id === periodId)

  return <section id="inicio" className="dashboard" aria-labelledby="dashboard-title">
    <div className="dashboard-heading">
      <div><p className="eyebrow">Controle de dados</p><h1 id="dashboard-title">Indicadores de atendimento</h1>
        <p>Resultados mensais e distribuição por operador e fila.</p></div>
      <button className="primary-button" disabled={loading} onClick={() => {
        setLoading(true); setError(false); setMetrics([]); setQueueId(''); setAttempt(value => value + 1)
      }}>Atualizar dados</button>
    </div>
    <div className="dashboard-filters">
      <label>Mês de referência<select aria-label="Mês de referência" value={periodId} disabled={loading || error || !periods.length} onChange={event => {
        if (event.target.value === periodId) return
        setLoading(true); setError(false); setMetrics([]); setQueueId(''); setPeriodId(event.target.value)
      }}>{!periods.length && <option value="">Nenhum período</option>}
        {periods.map(period => <option key={period.id} value={period.id}>{monthLabel(period.reference_month)}</option>)}
      </select></label>
      <label>Fila<select aria-label="Fila" value={queueId} disabled={loading || error} onChange={event => setQueueId(event.target.value)}>
        <option value="">Todas as filas</option>
        {queues.map(queue => <option key={queue.id} value={queue.id}>{queue.display_name}</option>)}
      </select></label>
      {selected && <span className="period-status">{statusLabel[selected.status]}</span>}
    </div>
    {loading ? <p role="status">Carregando indicadores…</p> : error ?
      <p className="form-error" role="alert">Não foi possível carregar os indicadores. Clique em Atualizar dados para tentar novamente.</p> :
      !metrics.length ? <div className="dashboard-empty"><h2>Nenhum indicador disponível</h2>
        <p>Os resultados aparecerão após a importação dos dados deste período.</p></div> : <>
        <dl className="metric-grid">
          <div><dt>Volume total</dt><dd>{number.format(totals.volume)}</dd><small>Inclui navegação automática</small></div>
          <div><dt>Navegação automática</dt><dd>{number.format(totals.navigation)}</dd><small>Default · sem TMA/TME</small></div>
          <div><dt>TMA</dt><dd>{duration(totals.tma)}</dd><small>{number.format(totals.tmaSamples)} amostras válidas</small></div>
          <div><dt>TME</dt><dd>{duration(totals.tme)}</dd><small>{number.format(totals.tmeSamples)} amostras válidas</small></div>
        </dl>
        <div className="dashboard-table" tabIndex={0} role="region" aria-label="Distribuição por operador e fila">
          <table><caption>Distribuição por operador e fila</caption>
            <thead><tr><th scope="col">Operador</th><th scope="col">Fila</th><th scope="col">Volume</th><th scope="col">TMA</th><th scope="col">TME</th></tr></thead>
            <tbody>{filtered.filter(metric => metric.operators !== null)
              .sort((a, b) => (a.operators!.display_name ?? a.operators!.external_name)
                .localeCompare(b.operators!.display_name ?? b.operators!.external_name, 'pt-BR') || a.queues.display_name.localeCompare(b.queues.display_name, 'pt-BR'))
              .map(metric => { const summary = summarize([metric]); return <tr key={metric.id}>
                <th scope="row">{metric.operators!.display_name ?? metric.operators!.external_name}</th>
                <td>{metric.queues.display_name}{metric.queues.kind === 'NAVIGATION' && <small> Navegação</small>}</td>
                <td>{number.format(summary.volume)}</td><td>{duration(summary.tma)}</td><td>{duration(summary.tme)}</td>
              </tr> })}</tbody>
          </table>
        </div>
        <p className="dashboard-note">Tempos em horas:minutos:segundos. “—” indica ausência de amostras válidas. Navegação automática não entra nas médias. Registros sem operador identificado permanecem no volume e não aparecem na distribuição por operador.</p>
      </>}
  </section>
}
